import { scaleLinear, scaleLog } from "d3";
import { get, uniq } from "lodash-es";
import { debounce } from "lodash-es";
import { createRef, PureComponent, type ReactElement, type SyntheticEvent } from "react";
import Measure from "react-measure";
import { connect } from "react-redux";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ErrorBar,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { CategoricalChartState } from "recharts/types/chart/generateCategoricalChart.js";
import { Center, LoadingBar, LoadingSpinner } from "widgets";
import { formatNumberWithPrecision } from "widgets/utils";
import { getCorrectedColor } from "widgets/utils";

import {
  ANALYTICAL_DATA_VIEWER_MAXIMAL_LIGHTNESS,
  CHART_TYPE,
  type ChartType,
  DATA_VIEWER_MODE,
} from "@/settings/settings.js";

import { calculateXAxisSettings, getYAxisDomain, type IXAxisSettings } from "../../app-utils/chart.js";
import { getDataSeriesKey, getDataSeriesLineKey } from "../../app-utils/chart-worker.js";
import type { ISelectionModel } from "../../app-utils/selection-model.js";
import type { Dictionary } from "../../app-utils/type.js";
import type { ChartLayoutState, ColumnAnnotation, DatasetColumn } from "../../graphql.js";
import {
  countNumberOfActiveFilters,
  getOrderedVisibleSelectedDataseries,
  type IDataSeriesMap,
  type IDatasetViewerState,
  orderDataSeriesConsideringSplitSeries,
  type SelectedDataSeries,
  setChartData,
  setChartSelection,
  shouldShowDataSeriesInLegend,
} from "../../redux/modules/dataset-viewer.js";
import type { IAppState } from "../../redux/reducer.js";
import { ContentPlaceholder } from "../core/ContentPlaceholder.js";
import CustomBar from "./CustomBar.js";
import CustomChartTooltip from "./CustomChartTooltip.js";
import CustomDot from "./CustomDot.js";
import CustomXAxisTick from "./CustomXAxisTick.js";
import Pixelator, { CHART_LEGEND_OPTION_HEIGHT } from "./Pixelator.js";
import ChartWorker from "./workers/chart.ts?worker&inline";

interface IDatasetChartOwnProps {}

interface IDatasetChartAppStateProps {
  isLoading: boolean;
  isParsingDataset: boolean;
  layout: ChartLayoutState;
  columns: DatasetColumn[];
  filteredOutColumns: string[];
  filteredOutValueIndicesByColumn: Dictionary<string[]>;
  activeFilter: boolean;
  dataSeriesMap: IDataSeriesMap;
  selectedAnnotations: ColumnAnnotation[];
  selectedDataSeries: SelectedDataSeries[];
  mode: number;
  chartSelection: string[];
  tableChartSelectionModel: ISelectionModel;
  chartData: IConditionCombination[];
  isComputingChartData: boolean;
}

interface IDatasetChartDispatchProps {
  setChartSelection: (selection: string[]) => void;
  setChartData: (data: ISetChartData) => void;
  hasDataSeriesAnyChartData: (dataSeries: SelectedDataSeries) => boolean;
}

export interface IDatasetChartProps
  extends IDatasetChartOwnProps,
    IDatasetChartDispatchProps,
    IDatasetChartAppStateProps {}

function mapStateToProps(state: IAppState, _ownProps: IDatasetChartOwnProps): IDatasetChartAppStateProps {
  const viewer: IDatasetViewerState = state.datasetViewer;
  const activeFilter: boolean = countNumberOfActiveFilters(viewer.filteredOutAnnotations) > 0;
  const isParsingDataset: boolean = viewer.selectedDataSeries.length === 0 && viewer.isLoadingDataSeries;

  return {
    isLoading: viewer.isLoading,
    isParsingDataset: isParsingDataset,
    layout: viewer.chartLayout,
    columns: viewer.dataset?.columns,
    filteredOutColumns: viewer.filteredOutColumns,
    filteredOutValueIndicesByColumn: viewer.filteredOutValueIndicesByColumn,
    activeFilter: activeFilter,
    dataSeriesMap: viewer.dataSeriesMap,
    selectedAnnotations: viewer.selectedAnnotations,
    selectedDataSeries: getOrderedVisibleSelectedDataseries(
      orderDataSeriesConsideringSplitSeries(
        viewer.selectedDataSeries.filter((s: SelectedDataSeries) => s.hidden !== true && s.isLoading !== true),
      ),
    ), // we filter out the data series that are split by annotation and order the others accordingly, we need this array to keep the same ref if nothing changes
    mode: viewer.mode,
    chartSelection: viewer.chartSelection,
    tableChartSelectionModel: viewer.chartTableSelectionModel,
    chartData: viewer.chartData,
    isComputingChartData: viewer.isComputingChartData,
  };
}

function mapDispatchToProps(dispatch): IDatasetChartDispatchProps {
  return {
    setChartSelection: (selection: string[]) => dispatch(setChartSelection(selection)),
    setChartData: (data: ISetChartData) => dispatch(setChartData(data)),
    hasDataSeriesAnyChartData: (dataSeries: SelectedDataSeries) => dispatch(shouldShowDataSeriesInLegend(dataSeries)),
  };
}

export interface IConditionCombination {
  name?: string | number;
  fields?: string[]; // will be needed to identify the user selection
  conditions: ICondition[];
  dataseries?: IDataSeriesDataMap;
  // for calculation of position of second condition labels
  firstInnerGroupSize?: number; // number of different conditions in the first level inner groups for second condition
  isFirstLeadingInner?: boolean; // true if this combination is the leading element of the first level inner group
  // for calculation of position of third condition labels
  secondInnerGroupSize?: number; // number of different conditions in the second level inner groups for third condition
  isSecondLeadingInner?: boolean; // true if this combination is the leading element of the second level inner group
}

export interface ICondition {
  key: string;
  value: string;
  label?: string;
  multiValueIndices?: string[]; // the same value of the same column might appear multiple times as a multi value
}

export type IDataSeriesDataMap = Record<string, IDataSeriesData>; // map by dataSeriesName

export interface IDataSeriesData {
  rawData: { key: string; value: number }[]; // raw data points corresponding to the combination of conditions/annotations
  //fields: Array<string>;      // will be needed to identify the user selection
  consolidatedValue?: number; // averaged value
  error?: number[]; // for error bars
}

export interface ISetChartData {
  chartData?: IConditionCombination[];
  isComputingChartData?: boolean;
}

interface IDatasetChartState {
  chartWidth: number;
  chartHeight: number;
}

class DatasetChart extends PureComponent<IDatasetChartProps, IDatasetChartState> {
  private static DEBOUNCE_TIMER = 200;
  private static NUMBER_PRECISION = 4;
  // private static MAX_CHART_COMPLEXITY: number = 2000000; // reasonable number of chart ticks on the x axis
  private static MAX_DATASERIES_COUNT = 200; // max number of data series, obtained via "split by" feature
  public static COLOR_SELECTION = "black";
  public static TABLE_COLOR_SELECTION = "#a6c3ea"; // original is too light "#eaf3ff";

  public static CHAR_WIDTH_IN_PX = 9;
  public static ROTATED_CHAR_HEIGHT_IN_PX = 4;
  public static AXIS_LABEL_HEIGHT = 35;
  public static MIN_BUCKET_WIDTH = 20;
  public static XAXIS_CHART_PADDING = 50;

  private composedChartRef = createRef<any>();
  private chartDataComputingWorker?: Worker = undefined;

  public static defaultBoxModelProps = {
    padding: {
      top: 50,
      bottom: 40,
      left: 30,
      right: 40,
    },
  };

  constructor(props: IDatasetChartProps) {
    super(props);

    this.state = {
      chartWidth: 0,
      chartHeight: 0,
    };
  }

  buildChartData = () => {
    // calculation of chart data would survive a huge number of data series, but the rendering would suffer too much
    // no need to compute the chart data
    if (this.props.selectedDataSeries.length > DatasetChart.MAX_DATASERIES_COUNT) {
      return;
    }

    this.props.setChartData({ isComputingChartData: true });
    // cancel the previous computation, if any
    if (this.chartDataComputingWorker) {
      this.chartDataComputingWorker.terminate();
    }
    // create a new instance of chart web worker
    this.chartDataComputingWorker = new ChartWorker();

    // what do we when getting back a response ?
    this.chartDataComputingWorker.onmessage = (event) => {
      this.chartDataComputingWorker!.terminate();
      this.chartDataComputingWorker = undefined;
      this.props.setChartData({ chartData: event.data, isComputingChartData: false });
    };

    // what do we do in case of worker/computation error ?
    this.chartDataComputingWorker.onerror = (_event) => {
      // Could not execute the chart worker
      this.chartDataComputingWorker!.terminate();
      this.chartDataComputingWorker = undefined;
      this.props.setChartData({ isComputingChartData: false });
    };

    // send the payload to the worker to start the computation
    this.chartDataComputingWorker.postMessage({
      selectedDataSeries: this.props.selectedDataSeries,
      selectedAnnotations: this.props.selectedAnnotations,
      dataSeriesMap: this.props.dataSeriesMap,
      columns: this.props.columns,
      filteredOutColumns: this.props.filteredOutColumns,
      filteredOutValueIndicesByColumn: this.props.filteredOutValueIndicesByColumn,
      layout: this.props.layout,
    } as IDatasetChartProps);
  };

  componentDidMount() {
    this.chartDimensionChanged = debounce(this.chartDimensionChanged, DatasetChart.DEBOUNCE_TIMER);
    this.buildChartData();
  }

  layoutModificationLeadsToBuildingChartDataAgain(layout: ChartLayoutState, previousLayout: ChartLayoutState) {
    return (
      previousLayout.filterOutEmptyValues !== layout.filterOutEmptyValues || // because we need to filter out some data
      previousLayout.isLogScale !== layout.isLogScale || // for calculation of y axis domain and scale
      previousLayout.chartType !== layout.chartType || // some dataseries inversion is happening
      previousLayout.timeSeries !== layout.timeSeries // used for parsing the data values
    );
  }

  componentDidUpdate(prevProps: IDatasetChartProps) {
    // if any of the props involved in the chart data computation did change then we trigger a new computation
    if (
      prevProps.selectedDataSeries !== this.props.selectedDataSeries ||
      prevProps.selectedAnnotations !== this.props.selectedAnnotations ||
      prevProps.dataSeriesMap !== this.props.dataSeriesMap ||
      prevProps.columns !== this.props.columns ||
      prevProps.filteredOutColumns !== this.props.filteredOutColumns ||
      prevProps.filteredOutValueIndicesByColumn !== this.props.filteredOutValueIndicesByColumn ||
      this.layoutModificationLeadsToBuildingChartDataAgain(this.props.layout, prevProps.layout)
    ) {
      this.buildChartData();
    }
  }

  componentWillUnmount() {
    if (this.chartDataComputingWorker) {
      this.chartDataComputingWorker.terminate();
    }
  }

  chartDimensionChanged(width: number, height: number) {
    this.setState({ chartWidth: width, chartHeight: height });
  }

  render() {
    const {
      selectedAnnotations,
      selectedDataSeries,
      columns,
      filteredOutColumns,
      isLoading,
      isParsingDataset,
      activeFilter,
    } = this.props;

    let content: ReactElement = null;
    if (isLoading) {
      content = <LoadingSpinner />;
    } else if (isParsingDataset) {
      content = (
        <Center>
          <LoadingBar isLoading />
          <ContentPlaceholder>Parsing the dataset...</ContentPlaceholder>
        </Center>
      );
    } else if (this.props.isComputingChartData) {
      content = (
        <Center>
          <LoadingBar isLoading />
          <ContentPlaceholder>Computing the chart data...</ContentPlaceholder>
        </Center>
      );
    } else if (columns?.length == filteredOutColumns?.length && activeFilter) {
      content = <ContentPlaceholder>No data to show : please adjust your filters</ContentPlaceholder>;
    } else if (!columns || columns.length === 0) {
      content = (
        <ContentPlaceholder>
          {this.props.mode === DATA_VIEWER_MODE.DATASET ? "No dataset selected" : "No data available for display"}
        </ContentPlaceholder>
      );
    } else if (!selectedAnnotations || selectedAnnotations.length === 0) {
      content = <ContentPlaceholder>No x-axis conditions selected</ContentPlaceholder>;
    } else if (selectedDataSeries.length === 0) {
      content = <ContentPlaceholder>No data series selected</ContentPlaceholder>;
      //} else if (calculateComplexityIndicator(this.props) > DatasetChart.MAX_CHART_COMPLEXITY) {
      //  content =  <ContentPlaceholder paddingTop={200} text={"Too much data to process : please adjust your selection of annotations and/or your filters"}/>;
    } else if (this.props.selectedDataSeries.length > DatasetChart.MAX_DATASERIES_COUNT) {
      // calculation of chart data would survive a huge number of data series, but the rendering would suffer too much (out of memory)
      content = <ContentPlaceholder text={"Too many data series : please adjust your Y-Axis selection"} />;
    } else {
      content = this.createChartWrapper();
    }

    return (
      <>
        <Measure
          bounds
          onResize={(contentRect) => {
            this.chartDimensionChanged(contentRect.bounds.width, contentRect.bounds.height);
          }}
        >
          {({ measureRef }) => (
            <div ref={measureRef} style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
              <ResponsiveContainer debounce={DatasetChart.DEBOUNCE_TIMER} ref={this.composedChartRef}>
                {content}
              </ResponsiveContainer>
            </div>
          )}
        </Measure>
        <Pixelator
          width={this.state.chartWidth}
          height={this.state.chartHeight + selectedDataSeries.length * CHART_LEGEND_OPTION_HEIGHT}
          getNodeRef={this.getComposedChartRef}
        />
      </>
    );
  }

  getComposedChartRef = () => {
    if (this.composedChartRef?.current) {
      return this.composedChartRef.current;
    }

    this.forceUpdate();
  };

  createChartWrapper(): ReactElement {
    // we need to clone the array, otherwise the reverse done for the line chart will modify the data series for other chart types
    const selectedDataSeries = this.props.selectedDataSeries.slice(0);

    const chartType: ChartType = this.props.layout.chartType as ChartType;

    // memoized/cached chart data, replaced by local state for now using componentDidUpdate :(
    //const data: Array<IConditionCombination> = getChartData(selectedDataSeries, this.props.selectedAnnotations, this.props.dataSeriesMap, this.props.columns, this.props.layout);
    const data: IConditionCombination[] = this.props.chartData;

    const margin: any = { ...DatasetChart.defaultBoxModelProps.padding };

    const axisSettings: IXAxisSettings = calculateXAxisSettings(
      data,
      this.props.selectedAnnotations.length,
      this.getChartWidth(),
    );
    margin.bottom = axisSettings.bottomMargin;

    const chartHeight: number = this.getChartHeight(margin.bottom);
    const yAxisDomain: [any, any] = getYAxisDomain(data, this.props.layout.isLogScale);
    const yAxisScale: any = (this.props.layout.isLogScale ? scaleLog() : scaleLinear())
      .domain(yAxisDomain)
      .range([0, chartHeight]);

    let chart: ReactElement[] = [];
    switch (chartType) {
      case CHART_TYPE.BAR:
        chart = this.createBarChart(selectedDataSeries, false, yAxisScale, chartHeight);
        break;
      case CHART_TYPE.STACKED_BAR:
        chart = this.createBarChart(selectedDataSeries, true, yAxisScale, chartHeight);
        break;
      case CHART_TYPE.LINE:
        chart = this.createLineChart(selectedDataSeries, data, yAxisScale, chartHeight);
        break;
      default:
        throw new Error(`The chart type ${this.props.layout.chartType} is not supported.`);
    }

    return (
      <ComposedChart
        key="composedChart"
        data={data}
        margin={margin}
        barSize={this.props.layout.timeSeries ? 20 : undefined}
        onClick={this.clearSelection}
      >
        {this.createXAxisComponent(data, axisSettings)}
        {this.createYAxisComponent(yAxisDomain)}
        <CartesianGrid key="grid" strokeDasharray="3 3" vertical={false} />
        <Legend
          verticalAlign="middle"
          wrapperStyle={{ display: "none" }}
          content={(
            _props /*  the legend is hidden on the page but should exist for the image export (see Pixelator) */,
          ) => (
            <svg>
              <g transform="translate(50,10)">
                {(chartType === CHART_TYPE.LINE ? selectedDataSeries.reverse() : selectedDataSeries)
                  .filter((ds) => this.props.hasDataSeriesAnyChartData(ds))
                  .map((dataseries: SelectedDataSeries, index) => (
                    <text
                      key={`legend-row-${index}`}
                      className={"legend"}
                      textAnchor="start"
                      alignmentBaseline="before-edge"
                    >
                      <tspan y={CHART_LEGEND_OPTION_HEIGHT * index + 1} fill={dataseries.color} fontSize="20px">
                        &#9632;{" "}
                      </tspan>
                      <tspan y={CHART_LEGEND_OPTION_HEIGHT * index} x="30">
                        {dataseries.name}
                      </tspan>
                    </text>
                  ))}
              </g>
            </svg>
          )}
        />
        <Tooltip
          key="tooltip"
          isAnimationActive={false}
          cursor={false}
          content={<CustomChartTooltip chartType={chartType} />}
        />
        {chart}
      </ComposedChart>
    );
  }

  handleBarClick = (data: any, _index: number, event: SyntheticEvent<any>) => {
    this.handleSelected(data.payload, event);
  };

  handleDotClick = (data: any, event: SyntheticEvent<any>) => {
    this.handleSelected(data.payload, event);
  };

  handleSelected = (payload: IConditionCombination, event: SyntheticEvent<any>) => {
    const additiveSelection: boolean = (event as any).ctrlKey; // get from original dom event
    if (additiveSelection) {
      this.props.setChartSelection(uniq(this.props.chartSelection.concat(payload.fields)));
    } else {
      this.props.setChartSelection(uniq(payload.fields));
    }
    // event.preventDefault();
    event.stopPropagation(); // so that the parent composedChart cannot catch the click and clear the selection
  };

  clearSelection = (_nextState: CategoricalChartState, event: SyntheticEvent<any>) => {
    if (!event || !(event as any).ctrlKey) {
      // if ctrl is clicked we do not reset since it is probably a mistake
      this.props.setChartSelection([]);
    }
  };

  allSelected = (superset: any[], subset: any[]) => {
    return subset.every((val) => superset.includes(val));
  };

  selectionCount = (superset: any[], subset: any[]) => {
    let count = 0;
    subset.forEach((val) => {
      if (superset.includes(val)) {
        count++;
      }
    });
    return count;
  };

  createBarChart = (
    visibleDataSeries: SelectedDataSeries[],
    stacked = false,
    yAxisScale: any,
    chartHeight: number,
  ): ReactElement[] => {
    const showErrorBars: boolean = this.props.layout.errorBars;
    return visibleDataSeries.map((series: SelectedDataSeries, index: number) => {
      const seriesKey: string = getDataSeriesKey(series.name);
      return (
        <Bar
          key={`bar-${index}`}
          name={series.name}
          dataKey={`dataseries.${seriesKey}.consolidatedValue`}
          stackId={stacked ? "stack-a" : null}
          fill={getCorrectedColor(series.color, ANALYTICAL_DATA_VIEWER_MAXIMAL_LIGHTNESS)}
          isAnimationActive={false}
          onClick={this.handleBarClick}
          shape={
            <CustomBar
              getSelectionCount={(fields) => this.selectionCount(this.props.chartSelection, fields)}
              getSelectedRawData={(fullData) => {
                if (stacked) {
                  // due to problem described in SEL6-376
                  return [];
                }
                return ((get(fullData, `dataseries.${seriesKey}.rawData`) as any[]) || []).filter(
                  (rawData: { key: any; value: number }) =>
                    this.props.tableChartSelectionModel.selectedIds.includes(rawData.key),
                );
              }}
              getSelectionYPosition={(value: number) =>
                chartHeight + DatasetChart.defaultBoxModelProps.padding.top - yAxisScale(value)
              }
            />
          }
        >
          {showErrorBars && (
            <ErrorBar
              key={`errorbar-${index}`}
              dataKey={`dataseries.${seriesKey}.error`}
              width={4}
              strokeWidth={1.5}
              stroke="black"
            />
          )}
        </Bar>
      );
    });
  };

  createLineChart = (
    visibleDataSeries: SelectedDataSeries[],
    data: IConditionCombination[],
    yAxisScale: any,
    chartHeight: number,
  ): ReactElement[] => {
    const showErrorBars: boolean = this.props.layout.errorBars;
    const lineCount: number = data.filter((comb: IConditionCombination) => comb.isFirstLeadingInner).length;
    const symbolSize: number = this.props.layout.symbolSize;
    const customStrokeWidth: number = symbolSize > 3 ? 2 : 1;

    let lines: ReactElement[] = [];

    for (let lineIndex = 0; lineIndex < lineCount; lineIndex++) {
      const groupLines: ReactElement[] = visibleDataSeries.map((series: SelectedDataSeries) => {
        const dataSeriesLineKey: string = getDataSeriesLineKey(series.name, lineIndex);
        return (
          <Line
            key={`line-${dataSeriesLineKey}`}
            name={series.name}
            type="linear"
            dataKey={`dataseries.${dataSeriesLineKey}.consolidatedValue`}
            fill={getCorrectedColor(series.color, ANALYTICAL_DATA_VIEWER_MAXIMAL_LIGHTNESS)}
            stroke={getCorrectedColor(series.color, ANALYTICAL_DATA_VIEWER_MAXIMAL_LIGHTNESS)}
            strokeWidth={customStrokeWidth}
            isAnimationActive={false}
            activeDot={{ onClick: this.handleDotClick }}
            dot={
              <CustomDot
                getSelectionCount={(fields) => this.selectionCount(this.props.chartSelection, fields)}
                getSelectedRawData={(fullData) => {
                  return (get(fullData, `dataseries.${dataSeriesLineKey}.rawData`) as any[]).filter(
                    (rawData: { key: any; value: number }) =>
                      this.props.tableChartSelectionModel.selectedIds.includes(rawData.key),
                  );
                }}
                getSelectionYPosition={(value: number) =>
                  chartHeight + DatasetChart.defaultBoxModelProps.padding.top - yAxisScale(value)
                }
                symbolSize={symbolSize}
              />
            }
            connectNulls={true}
          >
            {showErrorBars && (
              <ErrorBar
                key={`errorbar-${dataSeriesLineKey}`}
                dataKey={`dataseries.${dataSeriesLineKey}.error`}
                width={4}
                strokeWidth={1.5}
                stroke={series.color}
              />
            )}
          </Line>
        );
      });
      lines = lines.concat(groupLines);
    }

    return lines;
  };

  getChartWidth() {
    return (
      this.state.chartWidth -
      DatasetChart.defaultBoxModelProps.padding.left -
      DatasetChart.defaultBoxModelProps.padding.right -
      60
    ); // 60 is the space needed for the y-axis labels
  }

  getChartHeight(marginBottom: number) {
    // bottom margin is dynamic due to the nature of the multiple levels ticks
    return this.state.chartHeight - 28 - marginBottom - DatasetChart.defaultBoxModelProps.padding.top; // 28 corresponds to the inner padding for the ticks
  }

  createXAxisComponent(data: IConditionCombination[], axisSettings: IXAxisSettings): ReactElement {
    const layoutSettings: ChartLayoutState = this.props.layout;

    const xAxisType: "number" | "category" = layoutSettings.timeSeries ? "number" : "category";
    const xAxisDomain: any = layoutSettings.timeSeries ? ["auto", "auto"] : undefined;

    return (
      <XAxis
        key="xaxis"
        dataKey="name"
        tickLine={true}
        type={xAxisType}
        domain={xAxisDomain}
        label={this.createXAxisLabel() as any}
        interval={0}
        padding={
          layoutSettings.timeSeries && layoutSettings.chartType === CHART_TYPE.LINE
            ? { left: 0, right: 0 }
            : { left: DatasetChart.XAXIS_CHART_PADDING, right: DatasetChart.XAXIS_CHART_PADDING }
        }
        tick={
          <CustomXAxisTick
            axisSettings={axisSettings}
            data={data}
            selectedAnnotations={this.props.selectedAnnotations}
            layoutSettings={layoutSettings}
          />
        }
      />
    );
  }

  createYAxisComponent(yAxisDomain: [any, any]): ReactElement {
    const isLog: boolean = this.props.layout.isLogScale;

    const CustomYAxisTick = (props) => {
      const { x, y, payload } = props;

      if (!payload.isShow) {
        return null;
      }

      const roundedValue: number = formatNumberWithPrecision(payload.value, DatasetChart.NUMBER_PRECISION);
      return (
        <text
          x={x}
          y={y}
          stroke="none"
          fill="#666"
          className="recharts-text recharts-cartesian-axis-tick-value"
          textAnchor="end"
        >
          <tspan x={x} dy="0.355em">
            {roundedValue}
          </tspan>
        </text>
      );
    };

    return (
      <YAxis
        key="yaxis"
        label={this.createYAxisLabel()}
        scale={isLog ? "log" : "linear"}
        domain={yAxisDomain}
        tick={<CustomYAxisTick />}
      />
    );
  }

  createYAxisLabel() {
    return null;
    // return (
    //   <g transform={`translate(30 ${this.state.chartHeight / 2}) rotate(-90)`} className="axis-label">
    //     <text textAnchor="middle" style={{ alignmentBaseline: "top"}}>Expression</text>
    //   </g>
    // );
  }

  createXAxisLabel() {
    const selectedAnnotations: ColumnAnnotation[] = this.props.selectedAnnotations;
    const xAxisName: string =
      selectedAnnotations.length === 0 ? "" : selectedAnnotations.map((a: ColumnAnnotation) => a.name).join(", ");
    return (
      <g transform={`translate(${this.state.chartWidth / 2} ${this.state.chartHeight - 20})`} className="axis-label">
        <text textAnchor="middle" style={{ alignmentBaseline: "top" } as any}>
          {xAxisName}
        </text>
      </g>
    );
  }
}

export { DatasetChart };

export default connect<IDatasetChartAppStateProps, IDatasetChartDispatchProps, IDatasetChartOwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(DatasetChart);
